import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import ProjectPromotionStudio from './ProjectPromotionStudio';
import './ProjectMediaManager.css';

const COPY = {
  he: {
    title: 'מדיה של האתר',
    subtitle: 'החלפת תמונות בלי להפעיל את הסוכן. כל שינוי נשמר, נבדק בסביבת הבנייה ורק אז נפתח Viewer חדש.',
    empty: 'עדיין אין באתר אזורי תמונה ניתנים לעריכה. בבנייה הבאה הסוכן יסמן תמונות משמעותיות אוטומטית.',
    choose: 'החלף תמונה',
    loading: 'טוען אזורי מדיה…',
    uploading: 'מעדכן ומכין אימות…',
    verifying: 'בודק את השינוי ב־Cloud Sandbox…',
    ready: 'התמונה עודכנה וה־Viewer המאומת מוכן.',
    failed: 'לא ניתן להשלים את החלפת התמונה. המקור הקודם נשמר.',
    unavailable: 'האזור הזה עדיין לא ניתן להחלפה ישירה.',
    source: 'מקור',
    slot: 'אזור'
  },
  en: {
    title: 'Site media',
    subtitle: 'Replace customer images without invoking the agent. Every change is checkpointed, verified in the Cloud Sandbox, then opened in a fresh Viewer.',
    empty: 'This project has no editable media slots yet. New builds mark meaningful images automatically.',
    choose: 'Replace image',
    loading: 'Loading media slots…',
    uploading: 'Updating and preparing verification…',
    verifying: 'Verifying the change in the Cloud Sandbox…',
    ready: 'The image was updated and the verified Viewer is ready.',
    failed: 'The image could not be replaced. The previous source was preserved.',
    unavailable: 'This slot is not ready for direct replacement yet.',
    source: 'Source',
    slot: 'Slot'
  }
};

function language() {
  return /^he\b/i.test(document.documentElement.lang || navigator.language || '') ? 'he' : 'en';
}

function wait(ms) { return new Promise(resolve => setTimeout(resolve, ms)); }

export default function ProjectMediaManager({ project, onVerified }) {
  const copy = COPY[language()];
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeSlot, setActiveSlot] = useState(null);
  const [status, setStatus] = useState('');
  const inputRefs = useRef(new Map());

  const projectId = project?.id;
  const canManage = useMemo(() => Boolean(projectId && ['WEB', 'FULL_STACK'].includes(project?.target || 'WEB')), [projectId, project?.target]);

  const load = async () => {
    if (!projectId || !canManage) return;
    setLoading(true);
    setError('');
    try {
      const response = await api.projectMediaSlots(projectId);
      setSlots(response.slots || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || copy.failed);
      setSlots([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [projectId, canManage]);

  const replace = async (slot, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError(copy.failed);
      return;
    }
    setActiveSlot(slot.id);
    setError('');
    setStatus(copy.uploading);
    try {
      const change = await api.replaceProjectMedia(projectId, slot.id, file);
      setStatus(copy.verifying);
      for (let attempt = 0; attempt < 90; attempt++) {
        await wait(2000);
        const response = await api.executionJob(change.jobId);
        const state = response.job?.state;
        if (state === 'SUCCEEDED') {
          const session = await api.createSandboxPreviewSession(change.jobId);
          setStatus(copy.ready);
          await load();
          onVerified?.({ previewUrl: session.previewUrl, previewPages: session.previewPages || [], expiresAt: session.expiresAt, jobId: change.jobId, slotId: slot.id });
          return;
        }
        if (state === 'FAILED' || state === 'CANCELLED') throw new Error(response.job?.error || copy.failed);
      }
      throw new Error(copy.failed);
    } catch (err) {
      setError(err.response?.data?.error || err.message || copy.failed);
      setStatus('');
    } finally {
      setActiveSlot(null);
    }
  };

  if (!canManage) return null;

  return <>
    <section className="project-media-manager" aria-labelledby="project-media-title">
      <div className="project-media-heading">
        <div>
          <div className="project-media-eyebrow">MEDIA</div>
          <h3 id="project-media-title">{copy.title}</h3>
          <p>{copy.subtitle}</p>
        </div>
        <button type="button" className="project-media-refresh" onClick={load} disabled={loading || Boolean(activeSlot)} aria-label="Refresh media slots">↻</button>
      </div>

      {error && <div className="project-media-message error" role="alert">{error}</div>}
      {status && <div className="project-media-message" role="status">{status}</div>}
      {loading && <div className="project-media-empty">{copy.loading}</div>}
      {!loading && slots.length === 0 && !error && <div className="project-media-empty">{copy.empty}</div>}

      <div className="project-media-grid">
        {slots.map(slot => (
          <article className="project-media-card" key={slot.id}>
            <div className="project-media-preview" aria-hidden="true">
              {slot.src ? <img src={slot.src} alt="" onError={event => { event.currentTarget.style.visibility = 'hidden'; }} /> : <span>IMG</span>}
            </div>
            <div className="project-media-card-body">
              <div className="project-media-slot-name"><span>{copy.slot}</span><strong>{slot.id}</strong></div>
              <div className="project-media-source"><span>{copy.source}</span><code title={slot.src || slot.file}>{slot.src || slot.file}</code></div>
              {slot.editable ? (
                <>
                  <input
                    ref={node => { if (node) inputRefs.current.set(slot.id, node); }}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/avif,image/gif"
                    hidden
                    onChange={event => {
                      const [file] = event.target.files || [];
                      event.target.value = '';
                      replace(slot, file);
                    }}
                  />
                  <button type="button" className="project-media-replace" onClick={() => inputRefs.current.get(slot.id)?.click()} disabled={Boolean(activeSlot)}>
                    {activeSlot === slot.id ? copy.verifying : copy.choose}
                  </button>
                </>
              ) : <div className="project-media-unavailable">{slot.reason || copy.unavailable}</div>}
            </div>
          </article>
        ))}
      </div>
    </section>
    <ProjectPromotionStudio project={project} />
  </>;
}
